<template>
  <label
    class="zm-switch"
    :class="{
      'is-disabled': switchDisabled,
      'is-checked': checked,
      'is-large': size === 'large',
      'is-middle': size === 'middle',
      'is-small': size === 'small'
    }"
  >
    <input
      class="zm-switch__input"
      type="checkbox"
      @click="debouncedOnInputChange"
      ref="input"
      :id="id"
      :name="name"
      :true-value="activeValue"
      :false-value="inactiveValue"
      :disabled="switchDisabled"
      :tabindex="switchDisabled? '-1' : '0'"
      @keydown.enter="switchValue"
      autocomplete="off"
      role="button"
      data-toggle="button"
      :aria-pressed="checked.toString()"
      :aria-describedby="labelBy"
      :aria-label="labelText"
    >
    <span
      :class="['zm-switch__label', 'zm-switch__label--left', !checked ? 'is-active' : '']"
      v-if="inactiveIconClass || inactiveText"
    >
      <i :class="[inactiveIconClass]" v-if="inactiveIconClass" />
      <span v-if="!inactiveIconClass && inactiveText" :aria-hidden="checked">
        {{ inactiveText }}
      </span>
    </span>
    <span class="zm-switch__core" ref="core" />
    <span v-text="labelText" v-show="false" />
    <span
      :class="['zm-switch__label', 'zm-switch__label--right', checked ? 'is-active' : '']"
      v-if="activeIconClass || activeText"
    >
      <i :class="[activeIconClass]" v-if="activeIconClass" />
      <span v-if="!activeIconClass && activeText" :aria-hidden="!checked">
        {{ activeText }}
      </span>
    </span>
  </label>
</template>
<script>
  import Focus from '../../mixins/focus';
  import debounce from 'throttle-debounce/debounce';

  export default {
    name: 'ZmSwitch',
    mixins: [Focus('input')],
    inject: {
      ZmForm: {
        default: ''
      }
    },
    props: {
      value: {
        type: [Boolean, String, Number],
        default: false
      },
      disabled: {
        type: Boolean,
        default: false
      },
      size: {
        type: String,
        default: 'middle'
      },
      activeIconClass: {
        type: String,
        default: ''
      },
      inactiveIconClass: {
        type: String,
        default: ''
      },
      activeText: String,
      inactiveText: String,
      activeColor: {
        type: String,
        default: ''
      },
      inactiveColor: {
        type: String,
        default: ''
      },
      activeValue: {
        type: [Boolean, String, Number],
        default: true
      },
      inactiveValue: {
        type: [Boolean, String, Number],
        default: false
      },
      name: {
        type: String,
        default: ''
      },
      id: String,
      labelText: String,
      labelBy: String
    },
    data() {
      return {};
    },
    created() {
      if (!~[this.activeValue, this.inactiveValue].indexOf(this.value)) {
        this.$emit('input', this.inactiveValue);
      }
      this.debouncedOnInputChange = debounce(100, () => {
        this.handleChange();
      });
    },
    computed: {
      checked() {
        return this.value === this.activeValue;
      },
      switchDisabled() {
        return this.disabled || (this.ZmForm || {}).disabled;
      }
    },
    watch: {
      checked() {
        this.$refs.input.checked = this.checked;
        if (this.activeColor || this.inactiveColor) {
          this.setBackgroundColor();
        }
      }
    },
    methods: {
      handleChange() {
        const checked = !this.checked ? this.activeValue : this.inactiveValue;
        this.$emit('input', checked);
        this.$emit('change', checked);
        this.$nextTick(() => {
          // set input's checked property
          // in case parent refuses to change component's value
          this.$refs.input.checked = this.checked;
        });
      },
      setBackgroundColor() {
        let newColor = this.checked ? this.activeColor : this.inactiveColor;
        this.$refs.core.style.borderColor = newColor;
        this.$refs.core.style.backgroundColor = newColor;
      },
      switchValue() {
        !this.switchDisabled && this.handleChange();
      }
    },
    mounted() {
      /* istanbul ignore if */
      if (this.activeColor || this.inactiveColor) {
        this.setBackgroundColor();
      }
      this.$refs.input.checked = this.checked;
    }
  };
</script>

<style lang="scss">
  @import "./style";
</style>
